import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import {
    LayoutDashboard,
    Users,
    Network,
    Sparkles,
    Settings,
    LogOut,
    CreditCard,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';

const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Personas', href: '/personas', icon: Users },
    { name: 'Network', href: '/targets', icon: Network },
    { name: 'Studio', href: '/studio', icon: Sparkles },
    { name: 'Billing', href: '/plans', icon: CreditCard },
    { name: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
    className?: string;
    onClose?: () => void;
}

export function Sidebar({ className, onClose }: SidebarProps) {
    const { logout, user } = useAuthStore();
    const navItems =
        user?.role === 'admin' || user?.role === 'superadmin'
            ? [...navigation, { name: 'Admin', href: '/admin', icon: Users }]
            : navigation;

    return (
        <aside className={cn("flex h-full flex-col border-r-2 border-border bg-card", className)}>
            <div className="border-b-2 border-border px-5 py-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-border bg-primary text-lg font-black text-primary-foreground shadow-[3px_3px_0_hsl(var(--foreground)/0.22)] dark:shadow-[3px_3px_0_hsl(var(--primary)/0.22)]">
                        B
                    </div>
                    <div>
                        <p className="text-xl font-black tracking-[-0.08em]">beloud.pro</p>
                        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Command OS</p>
                    </div>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div className="p-4">
                    <div className="brutalist-panel-soft rounded-xl p-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <span className="brutalist-label">Credits</span>
                            <CreditCard className="h-4 w-4 text-primary" />
                        </div>
                        <div className="text-3xl font-black tracking-[-0.08em] text-primary">
                            {user?.creditsBalance?.toLocaleString() || 0}
                        </div>
                        <div className="mt-2 inline-flex rounded-full border border-border px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            Plan: {user?.plan || 'Free'}
                        </div>
                    </div>
                </div>

                <nav className="flex-1 space-y-1 px-4">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.name}
                                to={item.href}
                                onClick={onClose}
                                className={({ isActive }) =>
                                    cn(
                                        'group flex items-center gap-3 rounded-xl border-2 px-3 py-3 text-sm font-bold transition-all',
                                        isActive
                                            ? 'border-border bg-primary text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.18)] dark:shadow-[4px_4px_0_hsl(var(--primary)/0.22)]'
                                            : 'border-transparent text-muted-foreground hover:border-border hover:bg-secondary hover:text-foreground'
                                    )
                                }
                            >
                                <Icon className="h-4 w-4" />
                                {item.name}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="space-y-3 border-t-2 border-border p-4">
                    <ThemeToggle />
                    <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 font-bold"
                        onClick={() => logout()}
                    >
                        <LogOut className="h-4 w-4" />
                        Logout
                    </Button>
                </div>
            </div>
        </aside>
    );
}
